// src/pages/configuration-sidebar.tsx
import { useCallback, useMemo, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Button, Flex, Loader, View, Text } from "@aws-amplify/ui-react";
import { Auth } from "aws-amplify";

import { Container } from "../library/container";
import { ConfigurationAgentItem } from "../library/control/agent-listed";
import { useAgentApiAgentList } from "@heartcheck/shared-react";
import { enableConfigureAgents } from "../endpoints";
import type { Agent } from "@heartcheck/shared"; // ✅ add proper type

export function ConfigurationPage() {
  const agentListObject = useAgentApiAgentList();
  const [signingOut, setSigningOut] = useState(false);

  /**
   * Canonical sign-out landing page (static file on eu.heartcheckapp.com).
   * Ensure this exact URL is included in Cognito App Client logoutUrls.
   */
  const SIGNED_OUT_URL = "https://eu.heartcheckapp.com/signed-out";

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;

    setSigningOut(true);
    try {
      console.log("[Configuration] 🔐 Signing out via Auth.signOut()");
      await Auth.signOut();

      // Hosted UI may already redirect automatically
      window.location.assign(SIGNED_OUT_URL);
    } catch (err) {
      console.error("[Configuration] ❌ Sign out failed:", err);

      // Best-effort redirect
      window.location.assign(SIGNED_OUT_URL);
    } finally {
      setSigningOut(false);
    }
  }, [SIGNED_OUT_URL, signingOut]);

  const agentsRendered = useMemo(() => {
    const value = agentListObject.value;

    const agents: Agent[] =
      value && typeof value.items === "function" ? value.items() ?? [] : [];

    return agents.map((agent: Agent) => (
      <ConfigurationAgentItem agent={agent} key={agent.id} />
    ));
  }, [agentListObject.value]);

  if (agentListObject.isUnloaded() || !agentListObject.value) {
    return <Loader />;
  }

  if (!enableConfigureAgents) {
    return <Navigate to={"/chat"} />;
  }

  return (
    <>
      <View className="sidebar">
        <Container heading="Agents">
          <Flex direction="column">{agentsRendered}</Flex>
          <br />
        </Container>

        <Container heading="Authentication">
          <Flex direction="column">
            <Text padding={10}>Signed in</Text>
          </Flex>
          <br />
          <Button isFullWidth onClick={handleSignOut} isDisabled={signingOut}>
            {signingOut ? "Signing out…" : "Sign Out"}
          </Button>
        </Container>
      </View>

      <View className="body">
        <Outlet />
      </View>
    </>
  );
}
